/**
 * iter1040 — BHI Share Dialog.
 *
 * "Виж моят BHI" — generates a public token → shows a preview PNG + copy-link.
 * The public token is 30-day short-lived (backend `bhi_share_tokens` collection).
 * Used for WhatsApp / LinkedIn / X / Facebook (OG image auto-picks up on paste).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Copy, Check, Loader2, X, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BhiShareDialog({ isDark = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bhi/share-card/token`);
      setShare(data);
    } catch (e) {
      toast.error("Не успях да създам линк за споделяне.");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
    if (!share) generate();
  };

  const copyLink = async () => {
    if (!share?.public_url) return;
    const base = window.location.origin;
    const url = `${base}${share.public_url}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Линкът е копиран!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Не успях да копирам. Маркирай и копирай ръчно.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        data-testid="bhi-share-btn"
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border transition ${
          isDark ? "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20"
                 : "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100"
        }`}
        title="Сподели анонимизиран BHI резултат"
      >
        <Share2 className="w-3.5 h-3.5" />
        Сподели
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setOpen(false)}
             data-testid="bhi-share-dialog">
          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${
              isDark ? "bg-slate-900 border-slate-700 text-slate-100"
                     : "bg-white border-slate-200 text-slate-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-500/20"
              data-testid="bhi-share-dialog-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-5">
              <h3 className="text-base font-bold mb-2 flex items-center gap-2">
                <Share2 className="w-4 h-4 text-fuchsia-500" />
                Сподели твоя BHI резултат
              </h3>
              <p className="text-xs opacity-70 mb-4">
                Създава анонимизирано изображение с твоя BHI резултат + band. Линкът е валиден 30 дни. Може да го споделиш в WhatsApp, LinkedIn, X или Facebook.
              </p>

              {loading && (
                <div className="flex items-center gap-2 text-sm opacity-70">
                  <Loader2 className="w-4 h-4 animate-spin" /> Генерирам изображение…
                </div>
              )}

              {share && !loading && (
                <>
                  {/* Preview PNG */}
                  <div className={`rounded-xl overflow-hidden border mb-3 ${
                    isDark ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}>
                    <img
                      src={`${window.location.origin}${share.og_image}`}
                      alt="BHI резултат"
                      className="w-full h-auto block"
                      data-testid="bhi-share-preview-img"
                    />
                  </div>

                  <div className={`flex items-center gap-2 rounded-lg border p-2 ${
                    isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
                  }`}>
                    <ImageIcon className="w-4 h-4 shrink-0 opacity-60" />
                    <code className="text-[10px] flex-1 truncate">
                      {window.location.origin}{share.public_url}
                    </code>
                    <button
                      type="button"
                      onClick={copyLink}
                      data-testid="bhi-share-copy-btn"
                      className="inline-flex items-center gap-1 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-2 py-1 text-[11px] font-semibold"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? "Копирано" : "Копирай"}
                    </button>
                  </div>

                  <p className="text-[10px] opacity-60 mt-2">
                    Личните ти данни не се разкриват — само общ резултат и band.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
